// window.__cmx: test/debug hooks (used by the e2e suite).

import { conn } from './net/connection';
import { quality, type NetReport } from './net/quality';
import { telemetry } from './net/telemetry';
import { store, toast } from './store';
import type { MachineLoad, ServerEvent } from './net/protocol';
import { allTerms, onEvent, peekTerm } from './term/manager';
import { heardLayout } from './ui/useWorkspaceSync';
import { termCounters } from './term/SessionTerm';

declare global {
  interface Window {
    __cmx: ReturnType<typeof makeHooks>;
  }
}

// The workspace registers how to create a session (it owns the actions).
let createSession: ((command: string) => Promise<number | null>) | null = null;
export function setCreateSessionHook(fn: typeof createSession) {
  createSession = fn;
}

const frameCounts = new WeakMap<object, { all: number; full: number }>();

function makeHooks() {
  return {
    stats: () => telemetry.stats(),
    resetStats: () => telemetry.reset(),
    echoTraces: () => telemetry.echoTraces(),
    sessions: () => store.get().sessions,
    /** Show a toast (a session's needs-you toast takes `session`). */
    toast: (t: Parameters<typeof toast>[0], ttlMs = 0) => toast(t, ttlMs),
    /** Create a session running `command` (via /bin/sh -lc) and focus it; its id, or null. */
    newSession: (command: string) => (createSession ? createSession(command) : Promise.resolve(null)),
    sessionsLoaded: () => store.get().sessionsLoaded,
    focused: () => store.get().focusedSession,
    transport: () => conn.kind,
    state: () => store.get().conn,
    rtt: () => conn.lastRtt,
    /** The network's state and numbers (net/quality.ts); null before the hub's first report. */
    net: () => store.get().net,
    /** The machine's load as the agent last said it (spec "Machine load"). */
    machineLoad: () => store.get().machineLoad,
    /** Pretend the machine is under load (the banner's motion, without a machine that is). */
    setMachineLoad: (l: MachineLoad) => store.set({ machineLoad: l, loadHiddenAt: null }),
    /** The login form focused in the machine's browser (LoginOverlay), or null. */
    loginFields: () => store.get().loginFields,
    /** Where the desktop's picture is in the stream frame (CSS px), or null. */
    desktopView: () => store.get().desktopView,
    /** Feed a simulated network report taken at `now` (ms); live reports stop until reload. */
    netReport: (rep: Omit<NetReport, 'kind'>, now: number) => {
      quality.paused = true;
      quality.onReport({ kind: 'net', ...rep }, now, true);
    },
    ports: () => store.get().ports,
    /** The machine's workspaces as last heard (useWorkspaceSync), or null. */
    layout: () => heardLayout(),
    /** A control request on this page's connection (another app's layout.put, say), and its result. */
    control: (op: string, fields: Record<string, unknown> = {}) => conn.control(op, fields),
    /** Take `e` as if the machine had sent it (a `driven` event, which only an agent typing through the API sends). */
    event: (e: ServerEvent) => onEvent(e),
    /** Force-close the live transport; the client reconnects and re-attaches. */
    dropConnection: () => conn.drop(),
    /** Frames sent from now until dropConnection() are lost (a connection that is dying). */
    loseSends: () => conn.loseSends(),
    /** The connection goes silent both ways (a network change, a laptop asleep). */
    severConnection: () => conn.sever(),
    /** Input frames not yet acknowledged by the agent (default: focused session). */
    pendingInput: (sid?: number) => {
      const id = sid ?? store.get().focusedSession;
      return id === null ? 0 : (peekTerm(id)?.pendingInput ?? 0);
    },
    /** Durable-input mode of the current connection ('v2' when the agent acks input). */
    inputMode: () => conn.inputMode,
    /** What the machine's agent said it does (its hello): "desktop" when it has one. */
    features: () => conn.features,
    /** Plain text of a session's terminal buffer (default: focused session). */
    text: (sid?: number) => {
      const id = sid ?? store.get().focusedSession;
      return id === null ? '' : (peekTerm(id)?.text() ?? '');
    },
    offset: (sid?: number) => {
      const id = sid ?? store.get().focusedSession;
      const o = id === null ? null : peekTerm(id)?.offset;
      return o === null || o === undefined ? null : Number(o);
    },
    /** The xterm theme a session's terminal is using (default: focused session). */
    termTheme: (sid?: number) => {
      const id = sid ?? store.get().focusedSession;
      return id === null ? null : (peekTerm(id)?.term.options.theme ?? null);
    },
    renderers: () => Object.fromEntries(allTerms().map((t) => [t.sid, t.renderer])),
    flushTelemetry: () => telemetry.flush(),
    connects: () => conn.connects,
    counters: () => ({ ...termCounters }),
    /** Grid size of a session terminal (default: focused session). */
    dims: (sid?: number) => {
      const id = sid ?? store.get().focusedSession;
      const t = id === null ? undefined : peekTerm(id);
      return t ? { cols: t.term.cols, rows: t.term.rows } : null;
    },
    /** A session terminal's font size in px (its zoom included: term/zoom.ts). */
    fontSize: (sid: number) => peekTerm(sid)?.term.options.fontSize ?? null,
    /** Select everything in a session's terminal; what's selected ("" for nothing); whether its program takes the mouse. */
    selectAll: (sid: number) => peekTerm(sid)?.term.selectAll(),
    selection: (sid: number) => peekTerm(sid)?.term.getSelection() ?? null,
    mouseTracking: (sid: number) => (peekTerm(sid)?.term.modes.mouseTrackingMode ?? 'none') !== 'none',
    /**
     * Render audit (bench/term): from now on, at every paint of a session's terminal that shows
     * only flipbook letters (A-H) above the last row, note whether it shows more than one
     * (a frame half drawn).
     * Returns a function that stops and reports { paints, mixed }.
     */
    renderAudit: (sid: number) => {
      const t = peekTerm(sid);
      if (!t) return () => null;
      let paints = 0;
      let mixed = 0;
      const sub = t.term.onRender(() => {
        const b = t.term.buffer.active;
        const seen = new Set<string>();
        for (let y = 0; y < t.term.rows - 1; y++) {
          for (const ch of b.getLine(b.viewportY + y)?.translateToString(true) ?? '') if (ch !== ' ') seen.add(ch);
        }
        if (![...seen].every((c) => 'ABCDEFGH'.includes(c)) || !seen.size) return; // not a flipbook frame
        paints++;
        if (seen.size > 1) mixed++;
      });
      (window as unknown as { __cmxAuditStop?: () => { paints: number; mixed: number } }).__cmxAuditStop = () => {
        sub.dispose();
        return { paints, mixed };
      };
      return null;
    },
    /**
     * Counts the frames a session's terminal draws from the first call on, and those that draw
     * every row (a page shown again redraws its panes whole). Call again to read.
     */
    frames: (sid: number) => {
      const t = peekTerm(sid);
      if (!t) return null;
      let n = frameCounts.get(t.term);
      if (!n) {
        const c = { all: 0, full: 0 };
        t.term.onRender(({ start, end }) => {
          c.all++;
          if (start === 0 && end >= t.term.rows - 1) c.full++;
        });
        frameCounts.set(t.term, c);
        n = c;
      }
      return { ...n };
    },
    renderAuditStop: () => (window as unknown as { __cmxAuditStop?: () => { paints: number; mixed: number } }).__cmxAuditStop?.() ?? null,
    /** First buffer line shown in the viewport (scrollback position) and the bottom-most possible. */
    scroll: (sid?: number) => {
      const id = sid ?? store.get().focusedSession;
      const b = id === null ? undefined : peekTerm(id)?.term.buffer.active;
      return b ? { viewportY: b.viewportY, baseY: b.baseY } : null;
    },
  };
}

export function installDebugHooks() {
  window.__cmx = makeHooks();
}
